import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { ITableLargeColumnConfig } from 'projects/common/src/app/interfaces/table/table-large-column-config';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { excelExportDateFormat } from 'projects/common/src/app/services/utils/util.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { IComputerHistoryReportResponse } from '../../interfaces/computer-report';
import { ReportComputerHistoryService } from '../../services/report-computer-history';

@Component({
  selector: 'app-report-computer-history',
  templateUrl: './report-computer-history.component.html',
  styleUrls: ['./report-computer-history.component.scss'],
})
export class ReportComputerHistoryComponent implements OnInit {
  public selectedFilters: IOutputFilter;
  public params: IOutputFilter;
  public tableConfig: ITableConfig;
  public tableData: IComputerHistoryReportResponse[];
  public excludedColumns: string[];
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private layoutConfigService: LayoutConfigService,
    private languageService: LanguageService,
    private computerHistoryService: ReportComputerHistoryService,
    private tableService: TableConfigService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
    protected cdr: ChangeDetectorRef,
  ) {
    this.filterConfig = {
      dates: { required: true },
      cleanButton: true,
    };
    this.disabledExcelBtn = false;
  }

  ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'report-mtbox-history',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
    this.cdr.detectChanges();
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public async getReportByFilters(selectedFilters: IOutputFilter): Promise<void> {
    this.filterConfig.isLoading = true;
    this.selectedFilters = selectedFilters;

    this.params = this.computerHistoryService.setParams(selectedFilters);

    this.computerHistoryService
      .getComputerHistoryData(this.params)
      .then((resp) => {
        this.tableData = resp;
        this.tableData.forEach((element) => {
          element.computerCreatedAt = element.computerCreatedAt ? element.computerCreatedAt : null;
          element.computerCreatedByUser = element.computerCreatedByUser ? element.computerCreatedByUser : null;
          element.computerUpdatedAt = element.computerUpdatedAt ? element.computerUpdatedAt : null;
          element.computerUpdatedByUser = element.computerUpdatedByUser ? element.computerUpdatedByUser : null;
          element.reasonText = element.reasonText ? element.reasonText : null;
          element.statusName = element.isActive ? this.languageService.message('active') : this.languageService.message('inactive');
        });
        this.setTableConfig(this.tableData);
      })
      .finally(() => {
        this.filterConfig.isLoading = false;
      });
    const filters = {
      reports: 'report-computer-history',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * set table config
   */
  public setTableConfig(tableData: any[]): void {
    this.excludedColumns = ['computerHistoryId', 'isActive', 'createdAt', 'createdByUser'];
    const customLargeColumns: ITableLargeColumnConfig[] = [
      { field: 'computerId', isLargeColumn: true, flexGrow: 1, customPadding: '0px 10px 0px 0px' },
      { field: 'name', isLargeColumn: true, flexGrow: 1, customPadding: '0px 10px 0px 0px' },
      { field: 'ipAddress', isLargeColumn: true, flexGrow: 1, customPadding: '0px 10px 0px 0px' },
      { field: 'macAddress', isLargeColumn: true, flexGrow: 1, customPadding: '0px 0px 0px 0px' },
      { field: 'statusName', isLargeColumn: true, flexGrow: 0.5, customPadding: '0px 10px 0px 0px' },
      { field: 'computerCreatedAt', isLargeColumn: true, flexGrow: 1.5, customPadding: '0px 0px 0px 10px' },
      { field: 'computerCreatedByUser', isLargeColumn: true, flexGrow: 1, customPadding: '0px 0px 0px 10px' },
      { field: 'computerUpdatedAt', isLargeColumn: true, flexGrow: 2, customPadding: '0px 0px 0px 10px' },
      { field: 'computerUpdatedByUser', isLargeColumn: true, flexGrow: 1, customPadding: '0px 0px 0px 10px' },
      { field: 'reasonText', isLargeColumn: true, flexGrow: 0.8, customPadding: '0px 0px 0px 15px' },
    ];
    const headerParams: ITableHeaderParams = {
      data: tableData,
      customLargeColumns,
      sortables: true,
      excludeColumns: this.excludedColumns,
    };

    const datasetParams: ITableDataParams = {
      tableData,
      customLargeColumns,
      excludeColumns: this.excludedColumns,
      dateConfig: [
        { field: 'computerCreatedAt', format: 'date-time' },
        { field: 'computerUpdatedAt', format: 'date-time' },
      ],
    };

    const { header, dataset } = this.tableService.setTableMainConfig(headerParams, datasetParams);

    this.tableConfig = { header, dataset };
  }

  /**
   * Function to download report
   */
  public async downloadReport(): Promise<void> {
    const selectRangeDate = {
      startDate: excelExportDateFormat(this.selectedFilters.startDate),
      endDate: excelExportDateFormat(this.selectedFilters.endDate),
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.computerHistoryService.getComputerHistoryReportExcel(this.selectedFilters).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'mtbox-history-report-filename', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
